import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import {
  featureFlagCatalogEntry,
  VALVUR_FEATURE_FLAG_CATALOG,
} from '../constants/valvur-feature-flag-catalog.constants';
import { lastValueFrom, map, take } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface AdminFeatureFlag {
  key: string;
  enabled: boolean;
  description: string;
}

interface FeatureFlagsResponse {
  data?: AdminFeatureFlag[];
}

@Injectable({ providedIn: 'root' })
export class FeatureFlagsAdminService {
  #http = inject(HttpClient);

  async list(): Promise<AdminFeatureFlag[]> {
    const rows = await lastValueFrom(
      this.#http
        .get<FeatureFlagsResponse>(`${environment.baseUrl}/user/feature-flags`)
        .pipe(
          map((response) => response.data ?? []),
          take(1),
        ),
    );
    return this.#mergeWithCatalog(rows);
  }

  async get(key: string): Promise<AdminFeatureFlag> {
    return lastValueFrom(
      this.#http
        .get<AdminFeatureFlag>(
          `${environment.baseUrl}/user/feature-flags/${encodeURIComponent(key)}`,
        )
        .pipe(take(1)),
    );
  }

  async upsert(flag: AdminFeatureFlag): Promise<AdminFeatureFlag> {
    return lastValueFrom(
      this.#http
        .post<AdminFeatureFlag>(`${environment.baseUrl}/user/feature-flags`, {
          key: flag.key,
          enabled: flag.enabled,
          description: flag.description,
        })
        .pipe(take(1)),
    );
  }

  /** Catalog + DB rows, sorted by key. */
  mergeWithCatalog(rows: AdminFeatureFlag[]): AdminFeatureFlag[] {
    return this.#mergeWithCatalog(rows);
  }

  #mergeWithCatalog(rows: AdminFeatureFlag[]): AdminFeatureFlag[] {
    const byKey = new Map(rows.map((row) => [row.key, row]));
    const keys = new Set<string>([
      ...VALVUR_FEATURE_FLAG_CATALOG.map((entry) => entry.key),
      ...byKey.keys(),
    ]);

    return [...keys].sort().map((key) => {
      const stored = byKey.get(key);
      const catalog = featureFlagCatalogEntry(key);

      return {
        key,
        enabled: stored?.enabled ?? catalog?.defaultEnabled ?? false,
        description:
          stored?.description?.trim() || catalog?.description || '',
      };
    });
  }
}
